import { useEffect, useRef, useState } from "react";
import { MAX_DISCORD_CONTEXT_MAX_MESSAGES, MIN_DISCORD_CONTEXT_MAX_MESSAGES } from "../../shared/discord-context-policy";

import {
  adminContextPolicyDraftFromSnapshot,
  isAdminContextPolicyDraftDirty,
  parseAdminContextPolicyDraft,
  type AdminContextPolicyDraft,
} from "./admin-context-policy-form";
import { SettingsRequestError, settingsApi } from "./api";
import { Skeleton } from "./Skeleton";
import type { AdminContextPolicySnapshot } from "./types";
import "./admin-context-policy.css";

export function AdminContextPolicySettings() {
  const [snapshot, setSnapshot] = useState<AdminContextPolicySnapshot | null>(null);
  const [draft, setDraft] = useState<AdminContextPolicyDraft>({ contextMaxMessages: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const failedRequest = useRef<{ signature: string; requestId: string } | null>(null);

  async function reload(): Promise<void> {
    setLoading(true);
    setError("");
    setStatus("");
    try {
      const next = await settingsApi.loadAdminContextPolicy();
      setSnapshot(next);
      setDraft(adminContextPolicyDraftFromSnapshot(next));
      failedRequest.current = null;
    } catch {
      setSnapshot(null);
      setError("无法读取上下文追索设置。");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, []);

  function changeDraft(value: string): void {
    setDraft({ contextMaxMessages: value });
    setError("");
    setStatus("");
  }

  async function save(): Promise<void> {
    if (!snapshot) return;
    const parsed = parseAdminContextPolicyDraft(draft);
    if (!parsed.value) {
      setError(parsed.error ?? "上下文追索设置无效。");
      return;
    }
    const signature = String(parsed.value.context_max_messages);
    const requestId = failedRequest.current?.signature === signature
      ? failedRequest.current.requestId
      : crypto.randomUUID();
    setSaving(true);
    setError("");
    setStatus("");
    try {
      const next = await settingsApi.updateAdminContextPolicy({
        context_max_messages: parsed.value.context_max_messages,
        expected_revision: snapshot.revision,
        request_id: requestId,
      });
      setSnapshot(next);
      setDraft(adminContextPolicyDraftFromSnapshot(next));
      failedRequest.current = null;
      setStatus("最高追索消息数量已保存，并会动态应用到后续消息。");
    } catch (caught) {
      failedRequest.current = { signature, requestId };
      setError(caught instanceof SettingsRequestError && caught.code === "context_policy_revision_conflict"
        ? "设置已被其他管理员更新；草稿已保留，请重新打开本页核对后再保存。"
        : "保存失败，草稿已保留，请重试。");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="admin-context-policy" role="status" aria-label="正在读取上下文追索设置">
      <Skeleton width="38%" />
      <Skeleton variant="control" />
    </div>;
  }
  if (!snapshot) {
    return <div className="panel-error" role="alert"><p>{error}</p><button className="secondary outlined" type="button" onClick={() => void reload()}>重试</button></div>;
  }

  const parsed = parseAdminContextPolicyDraft(draft);
  const dirty = isAdminContextPolicyDraftDirty(snapshot, draft);
  const validationError = dirty ? parsed.error ?? "" : "";
  const contextMaxMessages = parsed.value?.context_max_messages ?? snapshot.context_max_messages;
  return <div className="admin-context-policy">
    <section className="admin-context-policy-card">
      <div>
        <h3>最高追索消息数量</h3>
        <p>设置机器人回复时最多采用多少条当前对话消息。数量包含本轮输入；其余消息只沿 Discord 原生回复链向前追索，不读取无关的频道历史。</p>
      </div>
      <label>最高消息条数
        <input
          aria-invalid={Boolean(validationError)}
          disabled={saving}
          inputMode="numeric"
          min={MIN_DISCORD_CONTEXT_MAX_MESSAGES}
          max={MAX_DISCORD_CONTEXT_MAX_MESSAGES}
          step="1"
          type="number"
          value={draft.contextMaxMessages}
          onChange={(event) => changeDraft(event.target.value)}
        />
      </label>
      <p className="admin-context-policy-preview">当前草稿允许本轮输入连同最多 <strong>{Math.max(0, contextMaxMessages - 1)} 条</strong>回复链祖先，共计不超过 <strong>{contextMaxMessages} 条</strong>消息。</p>
    </section>
    <div className="admin-context-policy-actions">
      <span className={error || validationError ? "action-error" : "action-success"} role={error || validationError ? "alert" : "status"} aria-live="polite">{error || validationError || status}</span>
      <button className="primary" type="button" disabled={saving || !dirty || !parsed.value} onClick={() => void save()}>{saving ? "保存中…" : "保存上下文设置"}</button>
    </div>
  </div>;
}
